import { useState } from 'react';
import * as DM from '@radix-ui/react-dropdown-menu';
import { ChevronDown, Printer } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import type { ListResult, ScreenDef } from '@/lib/types';
import { notifyDataChanged } from '@/grid/events';
import { confirmDialog } from '@/ui/confirm';
import { Dialog } from '@/ui/Dialog';
import { LookupDialog } from '@/grid/LookupDialog';
import { useWorkspace } from '@/shell/workspace';

type Row = Record<string, any>;

/**
 * Delphi Unit23 PopupMenu1: seviye TAG'ine göre görünen işlemler (cxGridActiveTabChanged).
 *  1 Verilenler   : İade Et, Ödeme Yap (Kasadan / Bankadan)
 *  2 Portföy      : Tahsil Et (Kasadan / Bankadan), Ciro Yap, Karşılıksızlara At, İade Et, Bankaya Gönder
 * Makbuz her seviyede (KAYNAK CARİ / CEK-SENET). Düzeltme yetkisi yoksa işlemler gizlenir (BitBtn5.Enabled).
 */
export function CekSenetIslemleri({ screen, levelTag, row }: { screen: ScreenDef; levelTag: number; row: Row | null }) {
  const ws = useWorkspace();
  const [banka, setBanka] = useState<null | 'tahsil' | 'odeme'>(null);
  const r = screen.rights;
  const id = row ? Number(row.ID) : 0;
  const yetki = r.update;
  const yenile = () => notifyDataChanged(screen.tag);
  const calis = async (fn: () => Promise<any>, ok: string) => {
    try { await fn(); toast.success(ok); yenile(); } catch (e: any) { toast.error(e.message); }
  };
  const makbuz = (rid: number, kayit: Row | null) => {
    const k = String(kayit?.KAYNAK ?? '').trim();
    if (kayit && k !== 'CARİ' && k !== 'CEK-SENET') { toast.error('Makbuz yalnız cari / çek-senet kayıtlarında basılır'); return; }
    const ad = String(kayit?.DURUM ?? '').trim() === 'Çek İade' ? 'Çek İade Makbuzu' : Number(kayit?.BORC) > 0 ? 'Tediye Makbuzu' : 'Tahsilat Makbuzu';
    ws.open({ kind: 'custom', component: 'HtmlOnizleme', title: `Yazdır · ${ad} ${String(kayit?.EVRAK_NO ?? '').trim()}`, props: { url: `/carifis/${rid}/makbuz/0?tag=${screen.tag}&ad=${encodeURIComponent(ad)}`, title: ad } }, { newInstance: true });
  };
  const ciro = async () => {
    try {
      const t = await api<Row>(`/ceksenet/${id}/ciro`, { method: 'POST' });
      ws.open({ kind: 'custom', component: 'CekSenetFormu', title: 'Çek/Senet · Ciro', props: { screen, taslak: t } }, { newInstance: true });
    } catch (e: any) { toast.error(e.message); }
  };
  const iade = async () => {
    try {
      const res = await api<{ id: number }>(`/ceksenet/${id}/iade`, { method: 'POST' });
      toast.success('İade kaydı oluşturuldu');
      yenile();
      if (await confirmDialog('Makbuz İstiyormusunuz !', { ok: 'Evet', cancel: 'Hayır' })) makbuz(res.id, { ...row, BORC: row?.ISLEM_CINSI === 'Firma Çeki' ? 0 : row?.ALACAK, KAYNAK: 'CEK-SENET' });
    } catch (e: any) { toast.error(e.message); }
  };

  const goster = { tahsil: levelTag === 2, ciro: levelTag === 2, karsiliksiz: levelTag === 2, iade: levelTag === 1 || levelTag === 2, odeme: levelTag === 1, bankaya: levelTag === 2 };
  const herhangi = yetki && Object.values(goster).some(Boolean);
  const item = 'menu-item';
  return (
    <>
      {herhangi && (
        <DM.Root>
          <DM.Trigger asChild><button className="btn" disabled={!row}>İşlemler <ChevronDown size={13} /></button></DM.Trigger>
          <DM.Portal>
            <DM.Content className="menu z-50 min-w-52 p-1" align="end">
              {goster.tahsil && <>
                <DM.Item className={item} onSelect={() => calis(() => api(`/ceksenet/${id}/kasa`, { body: { tur: 'tahsil' } }), 'Kasadan tahsil edildi')}>Tahsil Et › Kasadan</DM.Item>
                <DM.Item className={item} onSelect={() => setBanka('tahsil')}>Tahsil Et › Bankadan</DM.Item>
              </>}
              {goster.odeme && <>
                <DM.Item className={item} onSelect={() => calis(() => api(`/ceksenet/${id}/kasa`, { body: { tur: 'odeme' } }), 'Kasadan ödendi')}>Ödeme Yap › Kasadan</DM.Item>
                <DM.Item className={item} onSelect={() => setBanka('odeme')}>Ödeme Yap › Bankadan</DM.Item>
              </>}
              {goster.ciro && <DM.Item className={item} onSelect={ciro}>Ciro Yap</DM.Item>}
              {goster.karsiliksiz && <DM.Item className={item} onSelect={() => calis(() => api(`/ceksenet/${id}/durum`, { body: { durum: 'Karşılıksız' } }), 'Karşılıksızlara atıldı')}>Karşılıksızlara At</DM.Item>}
              {goster.bankaya && <DM.Item className={item} onSelect={() => calis(() => api(`/ceksenet/${id}/durum`, { body: { durum: 'Beklet' } }), 'Bankaya gönderildi')}>Bankaya Gönder</DM.Item>}
              {goster.iade && <DM.Item className={item} onSelect={iade}>İade Et</DM.Item>}
            </DM.Content>
          </DM.Portal>
        </DM.Root>
      )}
      <button className="btn" disabled={!row} onClick={() => row && makbuz(id, row)}><Printer size={14} /> Makbuz</button>
      {banka && <BankaSec tur={banka} onClose={() => setBanka(null)} onOk={async (b) => {
        await calis(() => api(`/ceksenet/${id}/banka`, { body: { tur: banka, ...b } }), banka === 'tahsil' ? 'Bankadan tahsil edildi' : 'Bankadan ödendi');
        setBanka(null);
      }} />}
    </>
  );
}

/** Delphi Unit23_2: banka carisi + muhasebe kodu / açıklama */
function BankaSec({ tur, onClose, onOk }: { tur: 'tahsil' | 'odeme'; onClose: () => void; onOk: (b: { bankaId: number; muhKodu: string; muhAciklama: string }) => void }) {
  const [b, setB] = useState<Row | null>(null);
  const [pick, setPick] = useState(false);
  const [muhKodu, setMuhKodu] = useState('');
  const [aciklama, setAciklama] = useState('');
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title={tur === 'tahsil' ? 'Bankadan Tahsil' : 'Bankadan Ödeme'} className="w-[min(520px,94vw)]"
      footer={<><button className="btn" onClick={onClose}>Vazgeç</button><button className="btn btn-primary" disabled={!b} onClick={() => b && onOk({ bankaId: Number(b.ID), muhKodu, muhAciklama: aciklama })}>Tamam</button></>}>
      <div className="flex flex-col gap-3 p-1">
        <label className="flex flex-col gap-1"><span className="h-label">Banka</span>
          <div className="flex gap-1"><input className="inp flex-1" readOnly value={b ? `${String(b.Kodu ?? '').trim()} · ${String(b['Ünvanı'] ?? '').trim()}` : ''} placeholder="Banka carisi seçiniz" />
            <button className="btn" onClick={() => setPick(true)}>Seç</button></div></label>
        <label className="flex flex-col gap-1"><span className="h-label">Muhasebe Kodu</span><input className="inp" value={muhKodu} onChange={(e) => setMuhKodu(e.target.value)} /></label>
        <label className="flex flex-col gap-1"><span className="h-label">Açıklama</span><input className="inp" maxLength={100} value={aciklama} onChange={(e) => setAciklama(e.target.value)} /></label>
      </div>
      {pick && <LookupDialog open row={{}} onClose={() => setPick(false)}
        spec={{ title: 'Banka Carileri', fetch: (q) => api<ListResult>('/ceksenet/banka-carileri', { body: { query: q } }), apply: (p) => p }}
        onPick={(p) => { setB(p); setPick(false); }} />}
    </Dialog>
  );
}
